import type { CommonScenario, ReportEditableFields, ReportSnapshot, RunSelection } from 'fuel-benchmark-contracts';
import { MAX_COMPARE_VEHICLES, MIN_COMPARE_VEHICLES, sameRunSelection } from '../compare/compare-selection';

export const REPORT_BUILDER_PATH = '/reports/new';

/**
 * `/reports/new?vehicleIds=a,b&scenarioId=x` — the hand-off the compare panel's "Generate report" button
 * produces. It pre-fills exactly **one** test: the vehicles and the compared scenario travel in the URL so the
 * builder is a shareable deep link, and the run selection travels in router state, because it is a per-vehicle
 * map that has no business in a query string (the builder falls back to every valid run when it is missing).
 * Further tests are added in the builder, which is where the scenario shortlist for these vehicles lives.
 *
 * The same link with no query at all — `REPORT_BUILDER_PATH` — is the standalone entry the reports list uses:
 * the builder then starts at its own vehicle step instead of a comparison (plan §7).
 */
export const reportBuilderLink = (vehicleIds: readonly string[], scenarioId: string): string =>
  `${REPORT_BUILDER_PATH}?vehicleIds=${vehicleIds.join(',')}&scenarioId=${encodeURIComponent(scenarioId)}`;

/** One test of the report being built: a scenario, and `null` for "every valid run" (plan §8). */
export interface BuilderTest {
  scenarioId: string;
  runIds: Record<string, string[]> | null;
}

export const runSelectionOf = (test: BuilderTest): RunSelection => test.runIds ?? 'ALL_VALID';

/**
 * The run selection handed over in router state, or `null` for "every valid run". Router state is `unknown`,
 * and a hand-typed URL carries none at all — so it is validated, not trusted.
 */
export const selectionFromState = (state: unknown): Record<string, string[]> | null => {
  if (state === null || typeof state !== 'object' || !('runSelection' in state)) {
    return null;
  }

  const selection = (state as { runSelection: unknown }).runSelection;

  if (selection === null || typeof selection !== 'object' || Array.isArray(selection)) {
    return null;
  }

  const entries = Object.entries(selection as Record<string, unknown>).filter(
    (entry): entry is [string, string[]] => Array.isArray(entry[1]) && entry[1].every((runId) => typeof runId === 'string'),
  );

  return entries.length === 0 ? null : Object.fromEntries(entries);
};

/** The most tests a report may span, mirroring `REPORT_MAX_TESTS` in the contract. */
export const MAX_TESTS = 6;

/** The most conclusions the builder may add, mirroring `REPORT_MAX_CONCLUSIONS` in the contract. */
export const MAX_CONCLUSIONS = 6;

/**
 * The test list the builder opens with. A compare hand-off names one scenario in the URL and carries its run
 * selection in router state, so that comparison becomes TEST 1; the standalone entry names none, and the
 * builder opens with an empty list the "Add a test" strip fills.
 */
export const seedTests = (scenarioId: string, routerState: unknown): BuilderTest[] =>
  scenarioId === '' ? [] : [{ scenarioId, runIds: selectionFromState(routerState) }];

/**
 * Two test lists are the same when they name the same scenarios in the same order and each keeps the same
 * runs. What the builder debounces on: a checkbox click must not fire a `reports.generate` per keystroke of
 * clicking, and re-rendering must not fire one at all.
 */
export const sameTests = (a: readonly BuilderTest[], b: readonly BuilderTest[]): boolean =>
  a.length === b.length && a.every((test, index) => test.scenarioId === b[index].scenarioId && sameRunSelection(test.runIds, b[index].runIds));

/**
 * Appending a test, at the cap and against duplicates: a scenario already in the report cannot be added twice,
 * and a seventh test is refused outright. A new test starts on every valid run, which is what the run-picker
 * beneath it then narrows.
 */
export const addTestTo = (tests: readonly BuilderTest[], scenarioId: string): BuilderTest[] =>
  tests.length >= MAX_TESTS || tests.some((test) => test.scenarioId === scenarioId) ? [...tests] : [...tests, { scenarioId, runIds: null }];

/** Removing a test. The list may empty — the builder's own "Add a test" strip is how it is refilled. */
export const removeTestAt = (tests: readonly BuilderTest[], index: number): BuilderTest[] => tests.filter((_, position) => position !== index);

/** Narrowing the runs behind one test, leaving every other test alone. */
export const withTestRuns = (tests: readonly BuilderTest[], index: number, runIds: Record<string, string[]>): BuilderTest[] =>
  tests.map((test, position) => (position === index ? { ...test, runIds } : test));

/**
 * What the "Add a test" strip may offer: every scenario all the selected vehicles have proven that is not
 * already a test of this report, and nothing at all once the cap is reached.
 *
 * This is the whole multi-test affordance in one function, which is why it is here rather than inline in the
 * page: a report is only ever single-test because this list came back empty.
 */
export const addableScenarios = (common: readonly CommonScenario[], tests: readonly BuilderTest[]): CommonScenario[] =>
  tests.length >= MAX_TESTS ? [] : common.filter((entry) => !tests.some((test) => test.scenarioId === entry.scenario.id));

/**
 * Which step the builder is on. The vehicles are chosen first because every test compares the same columns,
 * the tests second, and only a report that has both can be generated — so the builder is usable from a cold
 * `/reports/new` with no comparison behind it, which is what the reports list links to (plan §7).
 */
export type BuilderStep = 'VEHICLES' | 'TESTS' | 'DOCUMENT';

export const builderStep = (vehicleIds: readonly string[], tests: readonly BuilderTest[]): BuilderStep => {
  if (vehicleIds.length < MIN_COMPARE_VEHICLES || vehicleIds.length > MAX_COMPARE_VEHICLES) {
    return 'VEHICLES';
  }

  return tests.length === 0 ? 'TESTS' : 'DOCUMENT';
};

const sameList = (a: readonly string[], b: readonly string[] | undefined): boolean =>
  b !== undefined && a.length === b.length && a.every((entry, index) => entry === b[index]);

/**
 * Re-seeds the editors after the document has been regenerated — adding a test, or narrowing the runs behind
 * one, rewrites the sentences the generator would propose.
 *
 * A field the engineer has typed in is theirs and is never touched. A field still holding exactly what the
 * *previous* draft generated was never edited at all, so it takes the new draft's wording: otherwise a brief
 * that has grown to two tests would keep a takeaway written about one. The object identity is preserved when
 * nothing changed, so this can run on every draft without re-rendering the panel.
 */
export const refreshUntouchedEdits = (
  edits: ReportEditableFields,
  seeded: ReportEditableFields | null,
  generated: ReportEditableFields,
): ReportEditableFields => {
  if (seeded === null) {
    return edits;
  }

  const refreshed: ReportEditableFields = {
    title: edits.title === seeded.title ? generated.title : edits.title,
    takeaway: edits.takeaway === seeded.takeaway ? generated.takeaway : edits.takeaway,
    conclusions: sameList(edits.conclusions, seeded.conclusions) ? generated.conclusions : edits.conclusions,
  };

  const unchanged = refreshed.title === edits.title && refreshed.takeaway === edits.takeaway && sameList(refreshed.conclusions, edits.conclusions);

  return unchanged ? edits : refreshed;
};

/** A blank title or takeaway means "keep what the generator wrote", as `reports.save` resolves it too. */
const resolveText = (edited: string | undefined, generated: string): string => (edited && edited.trim() !== '' ? edited.trim() : generated);

/**
 * The texts the builder posts.
 *
 * Title and takeaway fall back to the generated sentence when they are left blank; conclusions deliberately
 * do **not**. The conclusion list is a list the human owns — emptying a row is how it is removed, and the
 * server drops blank entries for exactly that reason, so restoring the generated sentence here would fight
 * the user's edit. Before the first edit there is no list of the user's yet, so the generated one stands.
 */
export const resolveEdits = (generated: ReportEditableFields, edits: ReportEditableFields | null): ReportEditableFields => ({
  title: resolveText(edits?.title, generated.title),
  takeaway: resolveText(edits?.takeaway, generated.takeaway),
  conclusions: (edits?.conclusions ?? generated.conclusions).map((conclusion) => conclusion.trim()).filter((conclusion) => conclusion !== ''),
});

const twoDigits = (value: number): string => String(value).padStart(2, '0');

/**
 * The live preview: the draft snapshot with the editable texts swapped in and the conclusions renumbered.
 * Nothing else is touched — every number on the page still comes from the server's draft, and `reports.save`
 * recomputes the whole document anyway, so the preview can never invent a figure. An empty list previews the
 * document exactly as it will save: with no conclusions section at all.
 */
export const withEdits = (snapshot: ReportSnapshot, generated: ReportEditableFields, edits: ReportEditableFields | null): ReportSnapshot => {
  const resolved = resolveEdits(generated, edits);
  const items = resolved.conclusions.map((text, index) => ({ number: twoDigits(index + 1), text }));

  return {
    ...snapshot,
    title: resolved.title,
    page1: { ...snapshot.page1, title: resolved.title, takeaway: resolved.takeaway },
    results: {
      ...snapshot.results,
      conclusions: items.length === 0 || snapshot.results.conclusions === null ? null : { heading: snapshot.results.conclusions.heading, items },
    },
  };
};
